import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Home, Calendar, Map, Users, CreditCard, Bell, Settings as SettingsIcon,
  FileText, HandCoins, Shield, LogOut, Menu, X, Eye, ArrowLeft,
} from "lucide-react";
import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import Logo from "./Logo";

/* Admin tab links — each navigates to /admin?tab=<key>. The Admin page reads
   the `tab` query param and drives its <Tabs> state from it.
   The "Home" entry is the dedicated Home pill rendered separately; nav links
   start at Bookings to keep the constant lean and unambiguous. */
const ADMIN_LINKS = [
  { key: "bookings",      label: "Bookings",   icon: FileText,     testid: "admin-nav-bookings" },
  { key: "calendar",      label: "Calendar",   icon: Calendar,     testid: "admin-nav-calendar" },
  { key: "route",         label: "Route Map",  icon: Map,          testid: "admin-nav-route" },
  { key: "quotes",        label: "Quotes",     icon: HandCoins,    testid: "admin-nav-quotes" },
  { key: "customers",     label: "Customers",  icon: Users,        testid: "admin-nav-customers" },
  { key: "payments",      label: "Payments",   icon: CreditCard,   testid: "admin-nav-payments" },
  { key: "messages",      label: "Messages",   icon: Bell,         testid: "admin-nav-messages" },
  { key: "settings",      label: "Settings",   icon: SettingsIcon, testid: "admin-nav-settings" },
];

const ADMIN_BOTTOM_LINKS = [
  { key: "bookings", label: "Home",     icon: Home },
  { key: "bookings", label: "Bookings", icon: FileText },
  { key: "route",    label: "Route",    icon: Map },
  { key: "quotes",   label: "Quotes",   icon: HandCoins },
  { key: "settings", label: "Settings", icon: SettingsIcon },
];

function AdminTopNav() {
  const { logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // Active when either /admin path matches AND the current ?tab= matches.
  const currentTab = new URLSearchParams(location.search).get("tab") || "bookings";
  const isHome = location.pathname === "/admin" && currentTab === "bookings";

  return (
    <header className="sticky top-0 z-40 w-full backdrop-blur-xl bg-[#062818]/95 border-b border-emerald-900/40 text-white" data-testid="admin-top-nav">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <Link to="/admin?tab=bookings" className="flex items-center gap-2.5 group" data-testid="admin-brand-logo">
          <Logo size={36} />
          <div className="leading-tight">
            <div className="font-extrabold text-[15px] tracking-tight font-[Outfit]">JDSN</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-bold">Admin</div>
          </div>
          <span
            data-testid="admin-mode-badge"
            className="ml-2 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-400/30 text-emerald-100 text-[10px] uppercase tracking-[0.18em] font-extrabold"
          >
            <Shield className="w-3 h-3" /> Admin Mode
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 overflow-x-auto">
          {ADMIN_LINKS.map((l) => {
            const active = location.pathname === "/admin" && currentTab === l.key;
            return (
              <NavLink
                key={l.testid}
                to={`/admin?tab=${l.key}`}
                end
                data-testid={l.testid}
                className={`text-[12px] font-bold tracking-tight px-3 py-1.5 rounded-full transition-colors whitespace-nowrap ${active ? "bg-emerald-500/20 text-emerald-100" : "text-emerald-100/70 hover:text-white hover:bg-white/5"}`}
              >
                {l.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          {/* Quick "Home" pill mirrors the Home button request */}
          <NavLink
            to="/admin?tab=bookings"
            data-testid="admin-home-btn"
            className={`hidden sm:inline-flex items-center gap-1 px-3 h-9 rounded-full text-[12px] font-extrabold transition ${isHome ? "bg-emerald-500 text-[#04261C]" : "bg-emerald-500/15 text-emerald-100 hover:bg-emerald-500/25"}`}
          >
            <Home className="w-3.5 h-3.5" /> Home
          </NavLink>
          <Button onClick={logout} variant="ghost" size="sm" className="rounded-full text-emerald-100/80 hover:text-white hover:bg-white/10" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-1" /> Logout
          </Button>
          <button
            onClick={() => setOpen(!open)}
            data-testid="admin-mobile-menu-btn"
            className="lg:hidden p-2 text-white"
            aria-label="Open admin menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-emerald-900/50 bg-[#04261C]" data-testid="admin-mobile-menu">
          <div className="px-4 py-3 grid grid-cols-2 gap-1.5">
            {ADMIN_LINKS.slice(1).map((l) => {
              const active = location.pathname === "/admin" && currentTab === l.key;
              const Icon = l.icon;
              return (
                <NavLink
                  key={l.testid}
                  to={`/admin?tab=${l.key}`}
                  onClick={() => setOpen(false)}
                  className={`flex items-center gap-2 px-3 py-3 rounded-xl text-sm font-extrabold ${active ? "bg-emerald-500/20 text-emerald-100" : "text-emerald-100/80 hover:bg-white/5"}`}
                >
                  <Icon className="w-4 h-4" /> {l.label}
                </NavLink>
              );
            })}
          </div>
        </div>
      )}
    </header>
  );
}

function AdminBottomNav() {
  const location = useLocation();
  const currentTab = new URLSearchParams(location.search).get("tab") || "bookings";
  if (location.pathname.startsWith("/checkout/") || location.pathname.startsWith("/payment/")) return null;
  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#04261C] border-t border-emerald-900/40 text-emerald-100"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      data-testid="admin-bottom-nav"
    >
      <div className="grid grid-cols-5">
        {ADMIN_BOTTOM_LINKS.map(({ key, label, icon: Icon }, i) => {
          const active = location.pathname === "/admin" && currentTab === key;
          return (
            <Link
              key={`${key}-${i}`}
              to={`/admin?tab=${key}`}
              data-testid={`admin-bottom-${label.toLowerCase()}`}
              className={`flex flex-col items-center justify-center py-2.5 gap-0.5 transition-colors ${active ? "text-emerald-300" : "text-emerald-100/55"}`}
            >
              <Icon className={`w-5 h-5 ${active ? "stroke-[2.5]" : "stroke-2"}`} />
              <span className={`text-[10px] ${active ? "font-bold" : "font-medium"}`}>{label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

/* Renders when an admin clicked "Open Customer View" in Settings — sticky
   banner with a one-tap return-to-admin button. */
export function CustomerPreviewBanner() {
  const navigate = useNavigate();
  return (
    <div
      className="sticky top-0 z-50 bg-amber-500 text-amber-950 text-[12px] font-extrabold tracking-tight px-3 py-2 flex items-center justify-between gap-2 shadow"
      data-testid="customer-preview-banner"
    >
      <span className="inline-flex items-center gap-1.5">
        <Eye className="w-4 h-4" /> Customer preview · Admin Mode
      </span>
      <button
        type="button"
        onClick={() => {
          sessionStorage.removeItem("jdsn_customer_view_override");
          navigate("/admin?tab=bookings");
        }}
        data-testid="return-to-admin-btn"
        className="inline-flex items-center gap-1 bg-amber-950 text-amber-100 px-3 py-1.5 rounded-full hover:bg-amber-900 transition"
      >
        <ArrowLeft className="w-3 h-3" /> Return to Admin
      </button>
    </div>
  );
}

export default function AdminLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-[#F9FAFB]">
      <AdminTopNav />
      <main className="flex-1 pb-24 lg:pb-0">{children}</main>
      <AdminBottomNav />
    </div>
  );
}
